import { useMemo } from 'react'
import { FileText, Sparkles } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useSourceOfTruth } from '@/hooks/useSourceOfTruth'
import type { ContextGap } from '@/types/api'

interface NewChatViewProps {
  onSelectCategory: (category: string) => void
  gaps?: ContextGap[]
}

const categories = [
  { id: 'strategy', name: 'Strategy & Vision', desc: 'Mission, goals, long-term plans', isAction: false },
  { id: 'market', name: 'Market Research', desc: 'Competitors, trends, sizing', isAction: false },
  { id: 'product', name: 'Product', desc: 'Roadmap, features, specs', isAction: false },
  { id: 'customers', name: 'Customers', desc: 'Personas, feedback, interviews', isAction: false },
  { id: 'gtm', name: 'Go-to-Market', desc: 'Sales, marketing, distribution', isAction: false },
  { id: 'team', name: 'Team & Ops', desc: 'Org chart, hiring, culture', isAction: false },
  { id: 'financials', name: 'Financials', desc: 'Projections, metrics, unit economics', isAction: false },
  {
    id: 'general',
    name: 'Unstructured Inputs',
    desc: 'Upload interviews, sales calls, mentor notes...',
    isAction: true,
  },
] as const

type CategoryId = (typeof categories)[number]['id']

const CATEGORY_SECTIONS: Record<Exclude<CategoryId, 'general'>, string[]> = {
  strategy: ['mission'],
  market: ['targetMarket', 'competition'],
  product: ['product'],
  customers: ['targetMarket'],
  gtm: ['goToMarket'],
  team: ['team'],
  financials: ['businessModel', 'fundraising'],
}

const CATEGORY_GAP_DOMAINS: Record<Exclude<CategoryId, 'general'>, ContextGap['domain'][]> = {
  strategy: ['business_model'],
  market: ['business_model', 'go_to_market'],
  product: ['product'],
  customers: ['go_to_market'],
  gtm: ['go_to_market'],
  team: ['team'],
  financials: ['business_model'],
}

const MOCK_CATEGORY_COMPLETION: Record<Exclude<CategoryId, 'general'>, number> = {
  strategy: 84,
  market: 76,
  product: 58,
  customers: 63,
  gtm: 43,
  team: 68,
  financials: 55,
}

export function NewChatView({ onSelectCategory, gaps }: NewChatViewProps) {
  const location = useLocation()
  const { domains, loading } = useSourceOfTruth()
  const mockGapsEnabled = useMemo(() => {
    if (!import.meta.env.DEV) return false
    const params = new URLSearchParams(location.search)
    return params.get('mockGaps') === '1'
  }, [location.search])

  const domainsByKey = useMemo(
    () => new Map(domains.map((domain) => [domain.key, domain])),
    [domains]
  )

  const categoryGapCompletion = useMemo(() => {
    const map = new Map<string, number>()
    if (!gaps || gaps.length === 0) return map

    for (const [categoryId, domainKeys] of Object.entries(CATEGORY_GAP_DOMAINS)) {
      const scopedGaps = gaps.filter((gap) => domainKeys.includes(gap.domain))
      if (scopedGaps.length === 0) continue

      const resolved = scopedGaps.filter((gap) => gap.status === 'resolved').length
      map.set(categoryId, Math.round((resolved / scopedGaps.length) * 100))
    }

    return map
  }, [gaps])

  const getCategoryCompletion = (categoryId: CategoryId): number => {
    if (categoryId === 'general') return 100
    if (mockGapsEnabled) return MOCK_CATEGORY_COMPLETION[categoryId]

    const sectionKeys = CATEGORY_SECTIONS[categoryId]
    let filled = 0
    let total = 0

    for (const key of sectionKeys) {
      const domain = domainsByKey.get(key)
      if (!domain) continue
      filled += domain.filledFields
      total += domain.totalFields
    }

    const sectionCompletion = total > 0 ? Math.round((filled / total) * 100) : 0
    const gapCompletion = categoryGapCompletion.get(categoryId)

    if (gapCompletion == null) return sectionCompletion
    return Math.round(sectionCompletion * 0.6 + gapCompletion * 0.4)
  }

  return (
    <div className="w-full py-1 md:py-6">
      <div className="max-w-5xl mx-auto space-y-4 md:space-y-12">
        <div className="text-center space-y-1.5 md:space-y-2">
          <h2 className="text-xl md:text-3xl font-semibold">Start a new conversation</h2>
          <p className="text-xs md:text-base text-muted-foreground">
            Choose a topic to focus the context.
          </p>
          {mockGapsEnabled && (
            <p className="text-[11px] text-primary/90">Mock gap preview mode</p>
          )}
        </div>

        <div className="max-w-3xl mx-auto rounded-xl border border-[#dce3f1] dark:border-[#454a6d] bg-[#f7f9fe] dark:bg-[#23263b] px-3 py-2.5 md:px-4 md:py-3">
          <p className="text-[11px] md:text-xs text-[#64748b] dark:text-[#b2b8d1] text-center">
            Bars show gap completeness by category. Start a category chat and the AI will ask targeted questions to fill missing context.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-6">
          {categories.map((cat) => {
            const completion = getCategoryCompletion(cat.id)

            return (
              <button
                key={cat.id}
                onClick={() => onSelectCategory(cat.name)}
                className={cn(
                  'flex flex-row md:flex-col items-center md:items-start gap-2.5 md:gap-0 p-3 md:p-6 rounded-2xl border transition-all duration-200 text-left min-h-[96px] md:h-48 justify-start md:justify-between group relative overflow-hidden',
                  cat.isAction
                    ? 'bg-[#f7f8fc] border-[#e0e5f3] hover:bg-[#eef2fb] hover:border-[#c7d4f8] dark:bg-[#23263b] dark:border-[#454a6d] dark:hover:bg-[#31365a] dark:hover:border-[#5f6798]'
                    : 'bg-[#f3f5f8] border-[#f3f5f8] hover:bg-[#eef1f8] hover:border-[#c7d4f8] dark:bg-[#23263b] dark:border-[#454a6d] dark:hover:bg-[#31365a] dark:hover:border-[#5f6798]'
                )}
              >
                <div
                  className={cn(
                    'h-8 w-8 md:h-12 md:w-12 rounded-full flex items-center justify-center transition-colors md:mb-4 shrink-0',
                    cat.isAction
                      ? 'bg-white border border-[#dde3f2] text-primary group-hover:bg-primary group-hover:border-primary group-hover:text-white dark:bg-[#3a3f5c] dark:border-[#535a80] dark:text-primary dark:group-hover:bg-[#4f5882] dark:group-hover:border-[#6a74aa] dark:group-hover:text-white'
                      : 'bg-white border border-[#dde3f2] text-primary group-hover:bg-primary group-hover:border-primary group-hover:text-white dark:bg-[#3a3f5c] dark:border-[#535a80] dark:text-[#bcc4e3] dark:group-hover:bg-[#4f5882] dark:group-hover:border-[#6a74aa] dark:group-hover:text-white'
                  )}
                >
                  {cat.isAction ? (
                    <Sparkles className="h-4 w-4 md:h-6 md:w-6" />
                  ) : (
                    <FileText className="h-4 w-4 md:h-6 md:w-6" />
                  )}
                </div>
                <div className="min-w-0 flex-1 md:w-full md:flex md:flex-col md:h-full">
                  <div className="font-semibold text-xs sm:text-sm md:text-lg mb-0.5 md:mb-2 leading-tight">
                    {cat.name}
                  </div>
                  <p className="text-[10px] sm:text-[11px] md:text-xs text-muted-foreground leading-snug line-clamp-1 md:line-clamp-2">
                    {cat.desc}
                  </p>
                  {!cat.isAction && (
                    <div className="mt-2 md:mt-auto md:pt-3">
                      <div className="h-1.5 rounded-full bg-[#c8d0e4] dark:bg-white/15 overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all duration-500',
                            completion >= 75
                              ? 'bg-accent-success'
                              : completion >= 40
                                ? 'bg-accent-warning'
                                : 'bg-destructive'
                          )}
                          style={{
                            width: `${loading && !mockGapsEnabled ? 28 : Math.max(completion, 6)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
